import { ref } from 'vue';

/**
 * YouTube-style auto-hide for the game's chrome (status bar, control bar, back
 * button, connection indicator, and — on desktop — the persistent top nav).
 *
 * A module-level singleton rather than provide/inject: the desktop nav lives
 * in `App.vue`, a sibling of the routed game view, so nothing below the game
 * view could `provide()` something the nav can `inject()`. One game plays at a
 * time, so a single shared state is correct, not just convenient.
 */

const IDLE_HIDE_MS = 3000;

const visible = ref(true);
let hideTimer: number | null = null;
let isPlaying = false;
// Snapshotted at gesture start (see notePointerDown) so a completed tap can
// tell "was this already showing before I touched the screen" apart from
// "reveal() during this same gesture just turned it on" — both leave
// `visible` true by the time the tap is recognized, but only the former
// should toggle back off.
let visibleAtGestureStart = true;

function clearHideTimer() {
  if (hideTimer !== null) {
    window.clearTimeout(hideTimer);
    hideTimer = null;
  }
}

// Only armed while playing — paused chrome stays up until play is pressed again.
function scheduleHide() {
  clearHideTimer();
  if (!isPlaying) return;
  hideTimer = window.setTimeout(() => {
    visible.value = false;
  }, IDLE_HIDE_MS);
}

/** Tap, click, or mouse-move: show the chrome and (if playing) restart the idle clock. */
function reveal() {
  visible.value = true;
  scheduleHide();
}

function hideNow() {
  clearHideTimer();
  visible.value = false;
}

/**
 * Any pointerdown anywhere in the game area — buttons, the scrubber, the
 * sheet music itself — wakes the chrome up immediately, same as reveal().
 * The one thing it does extra is remember whether the chrome was *already*
 * showing before this press, for handleTap to use once the press resolves
 * into a genuine tap (see handleTap).
 */
function notePointerDown() {
  visibleAtGestureStart = visible.value;
  reveal();
}

/**
 * A press that resolved into a tap rather than a drag — the YouTube-style
 * "toggle the controls" gesture. If the chrome was already up before this tap
 * started, tapping again hides it immediately instead of just re-arming the
 * idle timer; if it was hidden, notePointerDown already revealed it and
 * there's nothing further to do. Only while playing — paused chrome always
 * stays up regardless of taps.
 */
function handleTap() {
  if (isPlaying && visibleAtGestureStart) {
    hideNow();
  }
}

/**
 * Play/pause toggles are the one place visibility changes without a gesture:
 * pressing play hides the chrome immediately, pressing pause brings it back
 * and keeps it up.
 */
function notifyPlaying(playing: boolean) {
  isPlaying = playing;
  if (playing) {
    hideNow();
  } else {
    reveal();
  }
}

/**
 * Leaving the game view must not strand the desktop nav (or anything else
 * reading `visible`) hidden on the next page — call on unmount.
 */
function reset() {
  clearHideTimer();
  isPlaying = false;
  visible.value = true;
}

export function useGameChromeVisibility() {
  return { visible, reveal, hideNow, notifyPlaying, reset, notePointerDown, handleTap };
}
